import { IconNames } from '@blueprintjs/icons';
import { NetworkStatuses, RouteCodeDisplayTextMap } from '@strata/constants';
import { SupplierCell } from '@strata/react-grid';
import { formatRateString } from '@strata/react-servicing-rate';
import { Icon } from '@strata/react-status-icons';
import { Tag } from '@strata/react-tags';
import { Tooltip } from '@strata/react-tooltip';
import PropTypes from 'prop-types';

function buildDisplayModel(offer, incumbentSupplierNpi, supplierMap) {
    const supplierNpi = offer?.parent_pharmacy_npi ?? offer?.transition_supplier;
    const isTransitionSupplier = Boolean(supplierNpi) && supplierNpi === offer?.transition_supplier;
    const supplier = supplierNpi ? supplierMap?.[supplierNpi] : undefined;
    const supplierDisplayName = supplier ? `${supplier.name} (${supplierNpi})` : supplierNpi;

    const showWarning =
        !isTransitionSupplier &&
        Boolean(incumbentSupplierNpi) &&
        Boolean(supplier) &&
        !supplier.children?.includes(incumbentSupplierNpi);
    const pharmacyLabel = isTransitionSupplier ? 'Transition Pharmacy' : 'Pharmacy';
    return { supplier, supplierDisplayName, showWarning, pharmacyLabel, isTransitionSupplier };
}

const PreferredPharmacyOfferTooltipContent = ({
    offer,
    pharmacyLabel,
    supplierDisplayName = '',
    showWarning,
}) => {
    const isGrandfathered = offer.grandfathered;
    const strategy = offer.referral_expiration_strategy;
    const needsReferenceDate = strategy === 'STAGGERED' || strategy === 'ON_DATE';
    const needsOverride = strategy === 'STAGGERED';
    return (
        <>
            <div className="font-semi-bold--capitalize">
                {RouteCodeDisplayTextMap[offer.route_code]}
            </div>
            <div className="flex__gap--x-small">
                {pharmacyLabel}: {supplierDisplayName}
                {showWarning && (
                    <>
                        <Icon icon={IconNames.WARNING_SIGN} />
                        NONINCUMBENT
                    </>
                )}
            </div>
            <div>Method: {offer.route_code}</div>
            <div>
                Rate:{' '}
                {formatRateString({
                    bidDecimal: offer.offer_rate?.discount ?? undefined,
                    referentPriceType: offer.offer_rate?.referent_price_type,
                })}
            </div>
            <div>{isGrandfathered ? 'Grandfathered' : ''}</div>
            {isGrandfathered && (
                <>
                    <div>Referral Expiration Strategy: {strategy}</div>
                    {needsReferenceDate && (
                        <div>
                            Referral Expiration Reference Date:{' '}
                            {offer.referral_expiration_reference_date}
                        </div>
                    )}
                    {needsOverride && (
                        <div>
                            Referral Expiration Override: {offer.referral_expiration_override}
                        </div>
                    )}
                </>
            )}
        </>
    );
};

PreferredPharmacyOfferTooltipContent.propTypes = {
    offer: PropTypes.shape({
        preferred_pharmacy_offer_id: PropTypes.string,
        parent_pharmacy_npi: PropTypes.string,
        transition_supplier: PropTypes.string,
        offer_rate: PropTypes.shape({
            referent_price_type: PropTypes.string,
            discount: PropTypes.number,
            price_is_frozen: PropTypes.bool,
        }),
        route_code: PropTypes.string,
        allow_route_override: PropTypes.bool,
        grandfathered: PropTypes.bool,
        referral_expiration_strategy: PropTypes.string,
        referral_expiration_reference_date: PropTypes.string,
        referral_expiration_override: PropTypes.string,
    }).isRequired,
    pharmacyLabel: PropTypes.string.isRequired,
    supplierDisplayName: PropTypes.string,
    showWarning: PropTypes.bool.isRequired,
};

function showGrandfatheredTag(offer, isTransitionSupplier) {
    if (!offer?.grandfathered) {
        return <div />;
    }
    return (
        <span>
            <Tag
                label="G"
                rightIcon={
                    isTransitionSupplier ? (
                        <Icon icon={IconNames.ARROW_RIGHT} size={10} />
                    ) : undefined
                }
            />
        </span>
    );
}

export const PreferredPharmacyOfferCell = ({
    offer = undefined,
    incumbentSupplierNpi = undefined,
    supplierMap = undefined,
}) => {
    const { supplier, supplierDisplayName, showWarning, pharmacyLabel, isTransitionSupplier } =
        buildDisplayModel(offer, incumbentSupplierNpi, supplierMap);

    const grandfatheredTag = showGrandfatheredTag(offer, isTransitionSupplier);
    return (
        <div className="flex__gap--x-small">
            {grandfatheredTag}
            <span style={{ overflow: 'hidden' }}>
                <SupplierCell
                    supplierName={supplierDisplayName}
                    lite={supplier?.networkStatus === NetworkStatuses.LITE}
                    intent={showWarning ? Tooltip.Intent.WARNING : undefined}
                    customTooltipContent={
                        offer ? (
                            <PreferredPharmacyOfferTooltipContent
                                offer={offer}
                                pharmacyLabel={pharmacyLabel}
                                supplierDisplayName={supplierDisplayName}
                                showWarning={showWarning}
                            />
                        ) : undefined
                    }
                />
            </span>
            {showWarning && (
                <span style={{ flexShrink: 0 }}>
                    <Icon icon={IconNames.WARNING_SIGN} intent={Icon.Intent.WARNING} />
                </span>
            )}
        </div>
    );
};

PreferredPharmacyOfferCell.propTypes = {
    offer: PropTypes.shape({
        preferred_pharmacy_offer_id: PropTypes.string,
        parent_pharmacy_npi: PropTypes.string,
        transition_supplier: PropTypes.string,
        offer_rate: PropTypes.shape({
            referent_price_type: PropTypes.string,
            discount: PropTypes.string,
            price_is_frozen: PropTypes.bool,
        }),
        route_code: PropTypes.string,
        allow_route_override: PropTypes.bool,
        grandfathered: PropTypes.bool,
        referral_expiration_strategy: PropTypes.string,
        referral_expiration_reference_date: PropTypes.string,
        referral_expiration_override: PropTypes.string,
    }),
    incumbentSupplierNpi: PropTypes.string,
    supplierMap: PropTypes.objectOf(
        PropTypes.shape({
            name: PropTypes.string,
            networkStatus: PropTypes.string,
            parentSupplierNpi: PropTypes.string,
            children: PropTypes.arrayOf(PropTypes.string),
        }),
    ),
};
